import React, { useEffect, useMemo, useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'motion/react';
import { getToken } from '../utils/auth';
import { useAuth } from '../context/AuthContext';
import {
  PageEnter,
  FadeIn,
  AnimatedNumber,
  AnimatedText,
  WelcomeLoading,
  cardVariants,
  staggerGridVariants,
  reducedVariants,
} from '../motion';
import usePrefersReducedMotion from '../motion/usePrefersReducedMotion';
import InlineLoading from '../components/ui/InlineLoading';
import {
  Users,
  Bell,
  CalendarDays,
  AlertTriangle,
} from 'lucide-react';

function cleanDisplayName(name) {
  return String(name || '')
    .replace(/^dr\.?\s+/i, '')
    .replace(/^nurse\s+/i, '')
    .trim();
}

function buildWelcomeGreeting({ name, role, userType }) {
  const display = cleanDisplayName(name);
  const roleNorm = String(role || '').trim().toLowerCase();
  const typeNorm = String(userType || '').trim().toLowerCase();

  if (typeNorm.includes('admin') || roleNorm.includes('admin')) {
    return {
      headline: display ? `Welcome back, ${display}` : 'Welcome back',
      subtitle: 'Administrator overview of the Remote Patient Monitoring System',
    };
  }

  if (roleNorm === 'doctor' || roleNorm.includes('doctor')) {
    return {
      headline: display ? `Welcome back, Dr. ${display}` : 'Welcome back, Doctor',
      subtitle: 'Overview of your Remote Patient Monitoring System',
    };
  }

  if (roleNorm === 'nurse' || roleNorm.includes('nurse')) {
    return {
      headline: display ? `Welcome back, Nurse ${display}` : 'Welcome back',
      subtitle: 'Overview of your Remote Patient Monitoring System',
    };
  }

  return {
    headline: display ? `Welcome back, ${display}` : 'Welcome back',
    subtitle: 'Overview of your Remote Patient Monitoring System',
  };
}

const StatCard = ({ to, icon: Icon, label, value, color }) => {
  const reduced = usePrefersReducedMotion();
  const variants = reducedVariants(cardVariants, reduced);

  return (
    <motion.div variants={variants}>
      <Link
        to={to}
        className={`group relative overflow-hidden ${color} text-white rounded-2xl p-8 transition-[box-shadow,transform] duration-200 hover:shadow-2xl active:scale-[0.99] cursor-pointer block`}
      >
        <div className="absolute inset-0 opacity-0 group-hover:opacity-10 bg-white transition-opacity" />
        <div className="relative flex items-end justify-between gap-4">
          <div className="flex flex-col min-w-0">
            <span className="text-sm font-medium opacity-90 mb-2">{label}</span>
            <span className="text-5xl font-bold tracking-tight tabular-nums">
              <AnimatedNumber value={value} />
            </span>
          </div>
          <div className="flex-shrink-0 rounded-2xl bg-white/15 p-3 opacity-90 group-hover:opacity-100 transition-opacity">
            <Icon className="w-10 h-10" strokeWidth={1.75} aria-hidden="true" />
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

const MyDashboard = () => {
  const API_BASE_URL = process.env.REACT_APP_API_URL;
  const { user, userType } = useAuth();
  const reduced = usePrefersReducedMotion();

  const [counts, setCounts] = useState({
    patients: 0,
    alerts: 0,
    appointments: 0,
  });
  const [welcome, setWelcome] = useState({
    status: 'loading',
    name: '',
    role: '',
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCounts = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = getToken();
        if (!token) {
          setError('No authentication token found');
          return;
        }

        const headers = { Authorization: `Bearer ${token}` };
        const [patientsRes, alertsRes, appointmentsRes] = await Promise.all([
          fetch(`${API_BASE_URL}/patients/`, { headers }),
          fetch(`${API_BASE_URL}/alerts/?limit=1000`, { headers }),
          fetch(`${API_BASE_URL}/appointments/with-relations/`, { headers }),
        ]);

        if (!patientsRes.ok || !alertsRes.ok || !appointmentsRes.ok) {
          throw new Error('Failed to fetch some counts');
        }

        const patientsData = await patientsRes.json();
        const alertsData = await alertsRes.json();
        const appointmentsData = await appointmentsRes.json();

        setCounts({
          patients: patientsData.length,
          alerts: alertsData.length,
          appointments: appointmentsData.length,
        });
      } catch (err) {
        setError(err.message || 'Error fetching data');
      } finally {
        setLoading(false);
      }
    };

    fetchCounts();
  }, [API_BASE_URL]);

  useEffect(() => {
    let cancelled = false;

    const loadWelcomeProfile = async () => {
      if (!user?.user_id || userType == null) return;

      const token = getToken();
      if (!token) {
        if (!cancelled) {
          setWelcome({ status: 'ready', name: '', role: '' });
        }
        return;
      }

      setWelcome({ status: 'loading', name: '', role: '' });

      const headers = { Authorization: `Bearer ${token}` };
      const type = String(userType || '').toLowerCase();

      try {
        const endpoint = type.includes('admin')
          ? `${API_BASE_URL}/admins/user/${user.user_id}`
          : `${API_BASE_URL}/clinicians/user/${user.user_id}`;
        const res = await fetch(endpoint, { headers });
        if (cancelled) return;

        if (!res.ok) {
          setWelcome({
            status: 'ready',
            name: '',
            role: type.includes('admin') ? 'Administrator' : '',
          });
          return;
        }

        const data = await res.json();
        if (cancelled) return;

        setWelcome({
          status: 'ready',
          name: cleanDisplayName(
            `${data.firstName || ''} ${data.lastName || ''}`.trim(),
          ),
          role: data.role || (type.includes('admin') ? 'Administrator' : ''),
        });
      } catch {
        if (!cancelled) {
          setWelcome({
            status: 'ready',
            name: '',
            role: type.includes('admin') ? 'Administrator' : '',
          });
        }
      }
    };

    loadWelcomeProfile();

    return () => {
      cancelled = true;
    };
  }, [API_BASE_URL, user?.user_id, userType]);

  const greeting = useMemo(() => {
    if (welcome.status !== 'ready') return null;
    return buildWelcomeGreeting({
      name: welcome.name,
      role: welcome.role,
      userType,
    });
  }, [welcome, userType]);

  const gridVariants = reducedVariants(staggerGridVariants, reduced);

  return (
    <div className="min-h-screen flex flex-col vitra-atmosphere">
      <Header />
      <PageEnter className="flex-grow w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12">
        <FadeIn className="mb-8 sm:mb-10 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div className="min-w-0 min-h-[5.5rem] sm:min-h-[6rem]">
            <AnimatePresence mode="wait">
              {greeting ? (
                <motion.div
                  key="welcome-ready"
                  initial={reduced ? false : { opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: reduced ? 0 : 0.28, ease: [0.23, 1, 0.32, 1] }}
                >
                  <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight">
                    <AnimatedText text={greeting.headline} className="block" as="span" />
                  </h1>
                  <AnimatedText
                    text={greeting.subtitle}
                    className="mt-2 block text-base sm:text-lg text-gray-600 max-w-2xl"
                    as="p"
                    delay={0.18}
                  />
                </motion.div>
              ) : (
                <WelcomeLoading key="welcome-loading" />
              )}
            </AnimatePresence>
          </div>
          <InlineLoading show={loading} label="Loading counts…" />
        </FadeIn>

        {error && (
          <div className="mb-6 flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <AlertTriangle className="h-5 w-5 shrink-0 text-amber-500" strokeWidth={1.75} />
            <span>{error}</span>
          </div>
        )}

        <motion.div
          className={`grid grid-cols-1 md:grid-cols-3 gap-6 mb-12 transition-opacity duration-200 ${
            loading ? 'opacity-70' : 'opacity-100'
          }`}
          variants={gridVariants}
          initial="hidden"
          animate="visible"
        >
          <StatCard
            to="/my-patients"
            icon={Users}
            label="Total Patients"
            value={counts.patients}
            color="bg-gradient-to-br from-teal-500 to-teal-600"
          />
          <StatCard
            to="/alerts"
            icon={Bell}
            label="Active Alerts"
            value={counts.alerts}
            color="bg-gradient-to-br from-rose-500 to-rose-600"
          />
          <StatCard
            to="/appointments"
            icon={CalendarDays}
            label="Appointments"
            value={counts.appointments}
            color="bg-gradient-to-br from-amber-500 to-amber-600"
          />
        </motion.div>

        <FadeIn className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-1">Quick Access</h2>
              <p className="text-gray-600">Navigate to key sections of your dashboard</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link
              to="/my-patients"
              className="group flex items-center gap-4 p-5 rounded-xl border border-gray-200 hover:border-teal-300 hover:bg-teal-50 transition-[background-color,border-color] duration-200 active:scale-[0.99]"
            >
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center text-teal-700 group-hover:bg-teal-200 transition-colors">
                <Users className="w-6 h-6" strokeWidth={1.75} />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 group-hover:text-teal-600 transition-colors">My Patients</h3>
                <p className="text-sm text-gray-600">View and manage patients</p>
              </div>
            </Link>

            <Link
              to="/alerts"
              className="group flex items-center gap-4 p-5 rounded-xl border border-gray-200 hover:border-rose-300 hover:bg-rose-50 transition-[background-color,border-color] duration-200 active:scale-[0.99]"
            >
              <div className="w-12 h-12 bg-rose-100 rounded-lg flex items-center justify-center text-rose-700 group-hover:bg-rose-200 transition-colors">
                <Bell className="w-6 h-6" strokeWidth={1.75} />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 group-hover:text-rose-600 transition-colors">Alerts</h3>
                <p className="text-sm text-gray-600">Check active alerts</p>
              </div>
            </Link>

            <Link
              to="/appointments"
              className="group flex items-center gap-4 p-5 rounded-xl border border-gray-200 hover:border-amber-300 hover:bg-amber-50 transition-[background-color,border-color] duration-200 active:scale-[0.99]"
            >
              <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center text-amber-700 group-hover:bg-amber-200 transition-colors">
                <CalendarDays className="w-6 h-6" strokeWidth={1.75} />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 group-hover:text-amber-600 transition-colors">Appointments</h3>
                <p className="text-sm text-gray-600">Upcoming schedules & online consultations</p>
              </div>
            </Link>
          </div>
        </FadeIn>
      </PageEnter>
      <Footer />
    </div>
  );
};

export default MyDashboard;
